import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gauge, Grid2x2, LineChart, Route, Sparkles, FileText, Scale, Lock } from "lucide-react";
import { useStrategy } from "@/context/StrategyContext";
import { Panel, Avatar, Bar, DemoBadge, Loading } from "@/components/strategy/ui";
import { fmtDate } from "@/lib/strategy";

const ANSWERS = ["What are we building?", "Who owns what?", "How do Growth and NicheCom work together?", "Where is our fastest route to revenue?",
  "Which products create long-term enterprise value?", "What must we agree before proceeding?", "What exactly should we do over the next 90 days?"];

const MODULES = [
  { to: "/roadmap/alignment", icon: Gauge, label: "Alignment dashboard", body: "Overall and section alignment, strongest agreements, biggest differences.", locked: true },
  { to: "/roadmap/decisions", icon: Scale, label: "Critical decisions", body: "Matters requiring formal agreement, with owners, status and dates.", locked: true },
  { to: "/roadmap/opportunities", icon: Grid2x2, label: "Opportunity engine", body: "Ten opportunities scored on ten criteria; impact vs effort.", locked: false },
  { to: "/roadmap/revenue", icon: LineChart, label: "Revenue model", body: "Conservative, base and ambitious scenarios with editable assumptions.", locked: false },
  { to: "/roadmap/plan", icon: Route, label: "Growth roadmap", body: "30 / 60 / 90 days and months 4–12, generated from the evidence.", locked: true },
  { to: "/roadmap/adviser", icon: Sparkles, label: "AI strategy adviser", body: "Ask grounded questions of the assessment, decisions and model.", locked: false },
  { to: "/roadmap/report", icon: FileText, label: "Executive report", body: "Downloadable PDF for the founders' strategy meeting.", locked: true },
];

export default function StrategyHome() {
  const { me, round } = useStrategy();
  if (!round) return <Loading />;
  const mine = round.statuses?.[me.id] || {};
  const ready = round.analysis_ready;

  return (
    <div className="space-y-8">
      <section className="st-panel st-grain relative overflow-hidden p-8 sm:p-12" data-testid="home-hero">
        <div className="blob" style={{ width: 520, height: 520, top: -260, right: -140, background: "radial-gradient(circle, rgba(200,247,74,.16), transparent 70%)" }} />
        <div className="relative z-10 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end">
          <div>
            <p className="st-caps mb-3">Founders' alignment &amp; strategy intelligence</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]"><span className="st-grad-text">GROWTH</span></h1>
            <p className="text-base md:text-lg font-semibold mt-3">Building the Intelligence Behind Better Business.</p>
            <p className="st-muted text-sm mt-4 max-w-xl leading-relaxed">A private strategic alignment, business planning and commercial opportunity tool for Jon, Andy and Pete. It exists to uncover differences before they become expensive mistakes — not to manufacture agreement.</p>
            <div className="flex flex-wrap gap-2 mt-7">
              {!round.demo && mine.status !== "submitted" && <Link to="/roadmap/assess" className="st-btn st-btn-primary" data-testid="home-continue">{mine.status === "draft" ? `Continue my assessment · ${mine.percent}%` : "Start my assessment"} <ArrowRight size={16} /></Link>}
              <Link to={ready ? "/roadmap/alignment" : "/roadmap/opportunities"} className="st-btn st-btn-ghost" data-testid="home-dashboard">{ready ? "Open the strategy dashboard" : "Explore the opportunity engine"}</Link>
            </div>
          </div>
          <div className="st-panel-2 p-5" data-testid="home-round-card">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div><p className="st-caps">Current round</p><p className="font-bold mt-1">{round.name}</p></div>
              {round.demo ? <DemoBadge /> : <span className="st-pill" style={{ background: "rgba(243,239,230,.07)", color: "var(--st-muted)" }}>{round.status}</span>}
            </div>
            <div className="space-y-3">
              {round.participants.map((p) => {
                const s = round.statuses[p.id] || {};
                return (
                  <div key={p.id} data-testid={`home-status-${p.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="flex items-center gap-2 text-sm font-semibold"><Avatar pid={p.id} size={22} />{p.name}{p.id === me.id && <span className="st-faint font-medium">(you)</span>}</span>
                      <span className="st-faint text-xs">{s.status === "submitted" ? `Submitted ${fmtDate(s.submitted_at)}` : s.status === "draft" ? `${s.percent}%` : "Not started"}</span>
                    </div>
                    <Bar value={s.percent} />
                  </div>
                );
              })}
            </div>
            <p className="st-faint text-xs mt-4 leading-relaxed">{ready ? "All assessments in — results are unlocked." : "Answers remain private until all three founders have submitted."}</p>
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-[1fr_1.6fr] gap-6">
        <Panel className="p-7">
          <p className="st-caps mb-3">This tool answers</p>
          <ol className="space-y-2.5">
            {ANSWERS.map((a, i) => <li key={a} className="flex gap-3 text-[15px] font-semibold"><span className="st-faint font-extrabold tabular-nums">{String(i + 1).padStart(2, "0")}</span>{a}</li>)}
          </ol>
          <p className="st-muted text-xs mt-6 leading-relaxed">Growth's independence and multi-industry ambition are established founding principles. The assessment tests the strategy for achieving them.</p>
        </Panel>
        <div className="grid sm:grid-cols-2 gap-4 st-stagger">
          {MODULES.map((m) => {
            const isLocked = m.locked && !ready;
            return (
              <Link key={m.to} to={m.to} className="st-panel p-5 flex flex-col gap-3 transition-transform hover:-translate-y-0.5" data-testid={`home-module-${m.to.split("/").pop()}`}>
                <div className="flex items-center justify-between">
                  <span className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: "var(--st-lime-dim)" }}><m.icon size={17} style={{ color: "var(--st-lime)" }} /></span>
                  {isLocked && <span className="st-pill st-faint" style={{ background: "rgba(243,239,230,.06)" }}><Lock size={10} /> Locked</span>}
                </div>
                <p className="font-bold">{m.label}</p>
                <p className="st-muted text-xs leading-relaxed">{m.body}</p>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
